import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Download, 
  Upload, 
  FileSpreadsheet, 
  Users, 
  Briefcase, 
  Building2, 
  DollarSign,
  Clock,
  ShoppingCart,
  CheckCircle,
  AlertTriangle,
  Loader2,
  FileUp,
  Edit,
  Trash2,
  X,
  AlertOctagon
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IMPORT_TYPES = [
  {
    id: 'customers',
    name: 'Customers',
    description: 'Import customer names with billing info',
    icon: Building2,
    color: 'bg-blue-100 text-blue-700',
    order: 1
  },
  {
    id: 'classifications',
    name: 'Classifications & Rates',
    description: 'Import classifications with ST/OT/DT rates (auto-creates crafts)',
    icon: DollarSign,
    color: 'bg-green-100 text-green-700',
    order: 2
  },
  {
    id: 'jobs',
    name: 'Jobs',
    description: 'Import jobs with PO numbers and values',
    icon: Briefcase,
    color: 'bg-purple-100 text-purple-700',
    order: 3
  },
  {
    id: 'crew_members',
    name: 'Crew Members',
    description: 'Import crew members with craft eligibility',
    icon: Users,
    color: 'bg-indigo-100 text-indigo-700',
    order: 4
  },
  {
    id: 'labor_entries',
    name: 'Labor Entries',
    description: 'Import timesheet data with hours',
    icon: Clock,
    color: 'bg-amber-100 text-amber-700',
    order: 5
  },
  {
    id: 'material_pos',
    name: 'Material POs',
    description: 'Import material purchase orders',
    icon: ShoppingCart,
    color: 'bg-teal-100 text-teal-700',
    order: 6
  }
];

export default function ImportData() {
  const { token, user } = useAuth();
  const [uploading, setUploading] = useState(null);
  const [results, setResults] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, file: null });
  const fileInputRefs = useRef({});
  
  // QuickBooks Import State
  const [qbFile, setQbFile] = useState(null);
  const [qbParsing, setQbParsing] = useState(false);
  const [qbPreview, setQbPreview] = useState(null);
  const [qbInvoices, setQbInvoices] = useState([]);
  const [qbSelected, setQbSelected] = useState(new Set());
  const [qbImporting, setQbImporting] = useState(false);
  const [jobs, setJobs] = useState([]);
  const qbFileInputRef = useRef(null);

  // Clear All Timesheets State
  const [clearTimesheetsDialog, setClearTimesheetsDialog] = useState(false);
  const [clearingTimesheets, setClearingTimesheets] = useState(false);
  
  // Cleanup Orphaned Data State
  const [cleanupOrphansDialog, setCleanupOrphansDialog] = useState(false);
  const [cleaningOrphans, setCleaningOrphans] = useState(false);
  
  // QuickBooks Online Sync State
  const [qboStatus, setQboStatus] = useState(null);
  const [qboSyncing, setQboSyncing] = useState(false);
  const [qboBills, setQboBills] = useState([]);
  const [qboPurchases, setQboPurchases] = useState([]);
  const [qboSelectedBills, setQboSelectedBills] = useState(new Set());
  const [qboSelectedPurchases, setQboSelectedPurchases] = useState(new Set());
  const [qboSyncTab, setQboSyncTab] = useState('bills');
  const [qboDateRange, setQboDateRange] = useState({ start: '', end: '' });
  const [pendingPOs, setPendingPOs] = useState([]);
  const [matchingBill, setMatchingBill] = useState(null);
  const [selectedPoForMatch, setSelectedPoForMatch] = useState('');
  const [matchingInProgress, setMatchingInProgress] = useState(false);
  
  const isOwner = user?.role === 'OWNER';

  // Fetch jobs for QuickBooks job matching
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await axios.get(`${API}/jobs`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setJobs(res.data || []);
      } catch (err) {
        console.error('Failed to fetch jobs:', err);
      }
    };
    if (token && user?.role !== 'FOREMAN') {
      fetchJobs();
    }
  }, [token, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch QBO connection status
  useEffect(() => {
    const fetchQboStatus = async () => {
      try {
        const res = await axios.get(`${API}/qbo/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setQboStatus(res.data);
      } catch (err) {
        console.error('Failed to fetch QBO status:', err);
        setQboStatus({ configured: false, connected: false });
      }
    };
    if (token && user?.role !== 'FOREMAN') {
      fetchQboStatus();
    }
  }, [token, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch bills from QuickBooks
  const handleFetchQboBills = async () => {
    setQboSyncing(true);
    try {
      const params = new URLSearchParams();
      if (qboDateRange.start) params.append('start_date', qboDateRange.start);
      if (qboDateRange.end) params.append('end_date', qboDateRange.end);
      
      const res = await axios.get(`${API}/qbo/bills?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQboBills(res.data.bills || []);
      setQboSelectedBills(new Set());
      toast.success(`Fetched ${res.data.count || 0} bills from QuickBooks`);
    } catch (err) {
      console.error('Failed to fetch QBO bills:', err);
      if (err.response?.data?.error === 'RECONNECT_REQUIRED') {
        toast.error('QuickBooks connection expired. Please reconnect in Settings.');
      } else {
        toast.error('Failed to fetch bills from QuickBooks');
      }
    } finally {
      setQboSyncing(false);
    }
  };

  // Fetch purchases/expenses from QuickBooks
  const handleFetchQboPurchases = async () => {
    setQboSyncing(true);
    try {
      const params = new URLSearchParams();
      if (qboDateRange.start) params.append('start_date', qboDateRange.start);
      if (qboDateRange.end) params.append('end_date', qboDateRange.end);
      
      const res = await axios.get(`${API}/qbo/purchases?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQboPurchases(res.data.purchases || []);
      setQboSelectedPurchases(new Set());
      toast.success(`Fetched ${res.data.count || 0} expenses from QuickBooks`);
    } catch (err) {
      console.error('Failed to fetch QBO purchases:', err);
      if (err.response?.data?.error === 'RECONNECT_REQUIRED') {
        toast.error('QuickBooks connection expired. Please reconnect in Settings.');
      } else {
        toast.error('Failed to fetch expenses from QuickBooks');
      }
    } finally {
      setQboSyncing(false);
    }
  };

  // Fetch pending Material POs for matching
  const fetchPendingPOs = async () => {
    try {
      const res = await axios.get(`${API}/qbo/pending-pos-for-matching`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingPOs(res.data.all || []);
    } catch (err) {
      console.error('Failed to fetch pending POs:', err);
    }
  };

  // Fetch pending POs when QBO is connected
  useEffect(() => {
    if (qboStatus?.connected) {
      fetchPendingPOs();
    }
  }, [qboStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open matching dialog for a bill
  const handleStartMatching = (bill) => {
    setMatchingBill(bill);
    setSelectedPoForMatch('');
  };

  // Match a QBO bill to a pending PO
  const handleMatchBillToPO = async () => {
    if (!matchingBill || !selectedPoForMatch) {
      toast.error('Please select a pending PO to match');
      return;
    }

    const selectedPO = pendingPOs.find(po => po.id === selectedPoForMatch);
    if (!selectedPO) {
      toast.error('Selected PO not found');
      return;
    }

    setMatchingInProgress(true);
    try {
      const res = await axios.post(`${API}/qbo/import-bill`, {
        qboBillId: matchingBill.qboId,
        qboDocNumber: matchingBill.docNumber,
        qboVendorName: matchingBill.vendorName,
        qboTxnDate: matchingBill.txnDate,
        qboTotalAmount: matchingBill.totalAmount,
        pendingPoId: selectedPoForMatch,
        jobId: selectedPO.jobId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(res.data.message || 'Bill matched successfully!');
      
      // Remove matched bill from list
      setQboBills(prev => prev.filter(b => b.qboId !== matchingBill.qboId));
      setMatchingBill(null);
      setSelectedPoForMatch('');
      
      // Refresh pending POs
      fetchPendingPOs();
    } catch (err) {
      console.error('Failed to match bill:', err);
      toast.error(err.response?.data?.detail || 'Failed to match bill to PO');
    } finally {
      setMatchingInProgress(false);
    }
  };

  const handleDownloadTemplate = async (typeId) => {
    try {
      const response = await axios.get(`${API}/import/template/${typeId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${typeId}_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${typeId.replace('_', ' ')} template downloaded`);
    } catch (error) {
      console.error('Failed to download template:', error);
      toast.error('Failed to download template');
    }
  };

  const handleClearAllTimesheets = async () => {
    setClearingTimesheets(true);
    try {
      const response = await axios.delete(`${API}/timesheets-bulk/clear-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { deleted } = response.data;
      toast.success(`Cleared ${deleted.timesheets} timesheets, ${deleted.laborEntries} labor entries, ${deleted.equipmentEntries} equipment entries`);
      setClearTimesheetsDialog(false);
    } catch (error) {
      console.error('Failed to clear timesheets:', error);
      toast.error(error.response?.data?.detail || 'Failed to clear timesheets');
    } finally {
      setClearingTimesheets(false);
    }
  };

  const handleCleanupOrphans = async () => {
    setCleaningOrphans(true);
    try {
      const response = await axios.delete(`${API}/data/cleanup-orphan-labor`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { deleted } = response.data;
      toast.success(`Cleaned up ${deleted.total} orphaned labor entries`);
      setCleanupOrphansDialog(false);
    } catch (error) {
      console.error('Failed to cleanup orphans:', error);
      toast.error(error.response?.data?.detail || 'Failed to cleanup orphaned data');
    } finally {
      setCleaningOrphans(false);
    }
  };

  const handleFileSelect = (typeId, event) => {
    const file = event.target.files?.[0];
    if (file) {
      setConfirmDialog({ open: true, type: typeId, file });
    }
  };

  const handleUpload = async () => {
    const { type, file } = confirmDialog;
    setConfirmDialog({ open: false, type: null, file: null });
    
    if (!file || !type) return;
    
    setUploading(type);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/import/${type}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setResults(prev => ({
        ...prev,
        [type]: response.data
      }));
      
      if (response.data.imported > 0) {
        toast.success(`Successfully imported ${response.data.imported} ${type.replace('_', ' ')}`);
      }
      
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
    } catch (error) {
      console.error('Failed to upload:', error);
      toast.error(error.response?.data?.detail || 'Failed to import data');
      setResults(prev => ({
        ...prev,
        [type]: { error: error.response?.data?.detail || 'Upload failed' }
      }));
    } finally {
      setUploading(null);
      // Reset file input
      if (fileInputRefs.current[type]) {
        fileInputRefs.current[type].value = '';
      }
    }
  };

  const getTypeInfo = (typeId) => IMPORT_TYPES.find(t => t.id === typeId);

  // QuickBooks Import Handlers
  const handleQbFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setQbFile(file);
      setQbPreview(null);
      setQbInvoices([]);
      setQbSelected(new Set());
    }
  };

  const handleQbParse = async () => {
    if (!qbFile) return;
    
    setQbParsing(true);
    try {
      const formData = new FormData();
      formData.append('file', qbFile);
      
      const res = await axios.post(`${API}/import/quickbooks/preview`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setQbPreview(res.data);
      setQbInvoices(res.data.invoices || []);
      // Auto-select all matched invoices
      const matched = new Set(
        res.data.invoices
          .filter(inv => inv.jobMatched)
          .map(inv => inv.rowIndex)
      );
      setQbSelected(matched);
      
      toast.success(`Parsed ${res.data.totalRows} invoices`);
    } catch (err) {
      console.error('Failed to parse QuickBooks file:', err);
      toast.error(err.response?.data?.detail || 'Failed to parse file');
    } finally {
      setQbParsing(false);
    }
  };

  const handleQbInvoiceChange = (rowIndex, field, value) => {
    setQbInvoices(prev => prev.map(inv => {
      if (inv.rowIndex === rowIndex) {
        const updated = { ...inv, [field]: value };
        // If job is being changed, update match status
        if (field === 'jobId') {
          updated.jobMatched = !!value;
          updated.errors = updated.errors.filter(e => !e.includes('not found'));
        }
        return updated;
      }
      return inv;
    }));
  };

  const toggleQbSelect = (rowIndex) => {
    setQbSelected(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowIndex)) {
        newSet.delete(rowIndex);
      } else {
        newSet.add(rowIndex);
      }
      return newSet;
    });
  };

  const toggleQbSelectAll = () => {
    if (qbSelected.size === qbInvoices.length) {
      setQbSelected(new Set());
    } else {
      setQbSelected(new Set(qbInvoices.map(inv => inv.rowIndex)));
    }
  };

  const handleQbImport = async () => {
    const selectedInvoices = qbInvoices.filter(inv => qbSelected.has(inv.rowIndex) && inv.jobId);
    
    if (selectedInvoices.length === 0) {
      toast.error('No valid invoices selected for import');
      return;
    }
    
    const missingJobs = selectedInvoices.filter(inv => !inv.jobId).length;
    if (missingJobs > 0) {
      toast.error(`${missingJobs} selected invoices are missing job assignments`);
      return;
    }
    
    setQbImporting(true);
    try {
      const payload = selectedInvoices.map(inv => ({
        jobId: inv.jobId,
        vendor: inv.vendor,
        date: inv.date,
        invoiceNumber: inv.invoiceNumber,
        type: inv.type,
        description: inv.description,
        amount: inv.amount
      }));
      
      const res = await axios.post(`${API}/import/quickbooks/confirm`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      toast.success(`Successfully imported ${res.data.imported} invoices`);
      
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} invoices had errors`);
      }
      
      // Reset QuickBooks state
      setQbFile(null);
      setQbPreview(null);
      setQbInvoices([]);
      setQbSelected(new Set());
      if (qbFileInputRef.current) {
        qbFileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Failed to import invoices:', err);
      toast.error(err.response?.data?.detail || 'Failed to import invoices');
    } finally {
      setQbImporting(false);
    }
  };

  const handleQbCancel = () => {
    setQbFile(null);
    setQbPreview(null);
    setQbInvoices([]);
    setQbSelected(new Set());
    if (qbFileInputRef.current) {
      qbFileInputRef.current.value = '';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <div className='p-4 md:p-8' data-testid='import-data-page'>
      <div className='mb-8'>
        <h1 className='text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900'>Import Data</h1>
        <p className='mt-2 text-lg text-slate-700'>
          Upload Excel files to bulk import your data. Download templates first to see the required format.
        </p>
      </div>

      {/* Import Order Notice */}
      <Card className='border-amber-200 bg-amber-50 mb-6 rounded-sm'>
        <CardContent className='p-4'>
          <div className='flex items-start gap-3'>
            <AlertTriangle className='w-5 h-5 text-amber-600 mt-0.5' />
            <div>
              <h3 className='font-bold text-amber-800'>Recommended Import Order</h3>
              <p className='text-sm text-amber-700 mt-1'>
                For best results, import data in this order: Customers → Classifications & Rates → Jobs → Crew Members → Labor Entries → Material POs.
                <strong> Classifications now auto-create crafts</strong> if they don't exist, so you don't need a separate crafts import.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* QuickBooks Online Direct Sync Section */}
      {user?.role !== 'FOREMAN' && qboStatus?.connected && (
        <Card className='border-emerald-300 bg-emerald-50/50 mb-6 rounded-sm' data-testid='qbo-sync-section'>
          <CardHeader className='pb-3'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-3'>
                <div className='p-2 rounded-sm bg-emerald-100 text-emerald-700'>
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h-2v-6h2v6zm0-8h-2V7h2v2zm4 8h-2v-4h2v4zm0-6h-2V9h2v2z"/>
                  </svg>
                </div>
                <div>
                  <CardTitle className='text-lg font-bold text-emerald-800'>QuickBooks Online Sync</CardTitle>
                  <CardDescription className='text-sm text-emerald-700'>
                    Pull bills and expenses directly from your connected QuickBooks account
                  </CardDescription>
                </div>
              </div>
              <Badge className='bg-emerald-100 text-emerald-800 rounded-sm'>
                <CheckCircle className='w-3 h-3 mr-1' />
                Connected
              </Badge>
            </div>
          </CardHeader>
          <CardContent className='space-y-4'>
            {/* Date Range Filter */}
            <div className='flex flex-wrap gap-4 items-end'>
              <div className='flex-1 min-w-[150px]'>
                <label className='text-sm font-medium text-slate-700 mb-1 block'>Start Date</label>
                <Input
                  type='date'
                  value={qboDateRange.start}
                  onChange={(e) => setQboDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className='rounded-sm'
                />
              </div>
              <div className='flex-1 min-w-[150px]'>
                <label className='text-sm font-medium text-slate-700 mb-1 block'>End Date</label>
                <Input
                  type='date'
                  value={qboDateRange.end}
                  onChange={(e) => setQboDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className='rounded-sm'
                />
              </div>
              <div className='flex gap-2'>
                <Button
                  onClick={handleFetchQboBills}
                  disabled={qboSyncing}
                  className='bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm'
                  data-testid='fetch-qbo-bills-btn'
                >
                  {qboSyncing ? <Loader2 className='w-4 h-4 animate-spin mr-2' /> : <Download className='w-4 h-4 mr-2' />}
                  Fetch Bills
                </Button>
                <Button
                  onClick={handleFetchQboPurchases}
                  disabled={qboSyncing}
                  variant='outline'
                  className='border-emerald-300 text-emerald-700 hover:bg-emerald-100 rounded-sm'
                  data-testid='fetch-qbo-purchases-btn'
                >
                  {qboSyncing ? <Loader2 className='w-4 h-4 animate-spin mr-2' /> : <Download className='w-4 h-4 mr-2' />}
                  Fetch Expenses
                </Button>
              </div>
            </div>

            {/* Bills Results */}
            {qboBills.length > 0 && (
              <div className='border border-emerald-200 rounded-sm p-4 bg-white'>
                <h4 className='font-bold text-slate-800 mb-3 flex items-center gap-2'>
                  <FileSpreadsheet className='w-4 h-4' />
                  Bills from QuickBooks ({qboBills.length})
                </h4>
                <div className='overflow-x-auto max-h-[400px] overflow-y-auto'>
                  <Table>
                    <TableHeader>
                      <TableRow className='bg-slate-50'>
                        <TableHead className='w-10'>
                          <Checkbox
                            checked={qboSelectedBills.size === qboBills.length && qboBills.length > 0}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setQboSelectedBills(new Set(qboBills.map(b => b.qboId)));
                              } else {
                                setQboSelectedBills(new Set());
                              }
                            }}
                          />
                        </TableHead>
                        <TableHead>Doc #</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className='text-right'>Amount</TableHead>
                        <TableHead>Line Items</TableHead>
                        <TableHead className='text-center'>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {qboBills.map((bill) => (
                        <TableRow key={bill.qboId} className='hover:bg-slate-50'>
                          <TableCell>
                            <Checkbox
                              checked={qboSelectedBills.has(bill.qboId)}
                              onCheckedChange={(checked) => {
                                const newSet = new Set(qboSelectedBills);
                                if (checked) {
                                  newSet.add(bill.qboId);
                                } else {
                                  newSet.delete(bill.qboId);
                                }
                                setQboSelectedBills(newSet);
                              }}
                            />
                          </TableCell>
                          <TableCell className='font-mono text-sm'>{bill.docNumber || '-'}</TableCell>
                          <TableCell className='font-medium'>{bill.vendorName}</TableCell>
                          <TableCell>{bill.txnDate}</TableCell>
                          <TableCell className='text-right font-mono font-bold'>
                            ${bill.totalAmount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell className='text-xs text-slate-500 max-w-[200px] truncate'>
                            {bill.lineItems?.map(li => li.description || li.accountName).filter(Boolean).join(', ') || '-'}
                          </TableCell>
                          <TableCell className='text-center'>
                            <Button
                              size='sm'
                              variant='outline'
                              onClick={() => handleStartMatching(bill)}
                              className='rounded-sm text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                              data-testid={`match-bill-${bill.qboId}`}
                            >
                              Match to PO
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className='mt-3 pt-3 border-t border-slate-200 flex justify-between items-center'>
                  <span className='text-sm text-slate-600'>
                    {qboSelectedBills.size} of {qboBills.length} bills | {pendingPOs.length} pending POs available for matching
                  </span>
                  <span className='text-sm text-emerald-600'>
                    Click "Match to PO" to link a bill to a foreman's pending request
                  </span>
                </div>
              </div>
            )}

            {/* Purchases/Expenses Results */}
            {qboPurchases.length > 0 && (
              <div className='border border-emerald-200 rounded-sm p-4 bg-white'>
                <h4 className='font-bold text-slate-800 mb-3 flex items-center gap-2'>
                  <ShoppingCart className='w-4 h-4' />
                  Expenses from QuickBooks ({qboPurchases.length})
                </h4>
                <div className='overflow-x-auto max-h-[400px] overflow-y-auto'>
                  <Table>
                    <TableHeader>
                      <TableRow className='bg-slate-50'>
                        <TableHead className='w-10'>
                          <Checkbox
                            checked={qboSelectedPurchases.size === qboPurchases.length && qboPurchases.length > 0}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setQboSelectedPurchases(new Set(qboPurchases.map(p => p.qboId)));
                              } else {
                                setQboSelectedPurchases(new Set());
                              }
                            }}
                          />
                        </TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Vendor/Entity</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className='text-right'>Amount</TableHead>
                        <TableHead>Line Items</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {qboPurchases.map((purchase) => (
                        <TableRow key={purchase.qboId} className='hover:bg-slate-50'>
                          <TableCell>
                            <Checkbox
                              checked={qboSelectedPurchases.has(purchase.qboId)}
                              onCheckedChange={(checked) => {
                                const newSet = new Set(qboSelectedPurchases);
                                if (checked) {
                                  newSet.add(purchase.qboId);
                                } else {
                                  newSet.delete(purchase.qboId);
                                }
                                setQboSelectedPurchases(newSet);
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Badge variant='outline' className='rounded-sm text-xs'>
                              {purchase.paymentType || 'Expense'}
                            </Badge>
                          </TableCell>
                          <TableCell className='font-medium'>{purchase.entityName}</TableCell>
                          <TableCell>{purchase.txnDate}</TableCell>
                          <TableCell className='text-right font-mono font-bold'>
                            ${purchase.totalAmount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell className='text-xs text-slate-500 max-w-[200px] truncate'>
                            {purchase.lineItems?.map(li => li.description || li.accountName).filter(Boolean).join(', ') || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className='mt-3 pt-3 border-t border-slate-200 flex justify-between items-center'>
                  <span className='text-sm text-slate-600'>
                    {qboSelectedPurchases.size} of {qboPurchases.length} selected
                  </span>
                  <div className='text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-sm'>
                    <AlertTriangle className='w-3 h-3 inline mr-1' />
                    Import feature coming soon - Expenses preview only
                  </div>
                </div>
              </div>
            )}

            {/* Empty State */}
            {qboBills.length === 0 && qboPurchases.length === 0 && (
              <div className='text-center py-8 text-slate-500'>
                <Download className='w-8 h-8 mx-auto mb-2 opacity-50' />
                <p>Click "Fetch Bills" or "Fetch Expenses" to pull data from QuickBooks</p>
                <p className='text-xs mt-1'>Optionally set date range to filter results</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* QuickBooks Invoice Import Section */}
      {user?.role !== 'FOREMAN' && (
        <Card className='border-green-200 bg-green-50/50 mb-6 rounded-sm' data-testid='quickbooks-import-section'>
          <CardHeader className='pb-3'>
            <div className='flex items-center gap-3'>
              <div className='p-2 rounded-sm bg-green-100 text-green-700'>
                <FileSpreadsheet className='w-5 h-5' />
              </div>
              <div>
                <CardTitle className='text-lg font-bold'>QuickBooks Invoice Import</CardTitle>
                <CardDescription className='text-sm'>
                  Import vendor bills from a QuickBooks "Project Costs Detail by Vendor" Excel export.
                  <br />
                  <span className='text-xs text-slate-500 mt-1 inline-block'>
                    Required columns: Project, Vendor, Date, Num (Invoice #), Memo/Description, Amount.
                    Job numbers must match your system (e.g., "JOB-2024-001").
                  </span>
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className='space-y-4'>
            {/* File Upload Section */}
            {!qbPreview && (
              <div className='flex flex-col sm:flex-row gap-3 items-start sm:items-center'>
                <input
                  type='file'
                  accept='.xlsx,.xls'
                  onChange={handleQbFileSelect}
                  ref={qbFileInputRef}
                  className='hidden'
                  id='qb-file-input'
                  data-testid='qb-file-input'
                />
                <Button
                  onClick={() => qbFileInputRef.current?.click()}
                  variant='outline'
                  className='border-2 border-green-300 hover:border-green-500 bg-white rounded-sm'
                  data-testid='qb-select-file-btn'
                >
                  <FileUp className='w-4 h-4 mr-2' />
                  Select Excel File
                </Button>
                
                {qbFile && (
                  <>
                    <span className='text-sm text-slate-600 flex items-center gap-2'>
                      <FileSpreadsheet className='w-4 h-4 text-green-600' />
                      {qbFile.name}
                    </span>
                    <Button
                      onClick={handleQbParse}
                      disabled={qbParsing}
                      className='bg-green-600 hover:bg-green-700 text-white rounded-sm'
                      data-testid='qb-parse-btn'
                    >
                      {qbParsing ? (
                        <>
                          <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                          Parsing...
                        </>
                      ) : (
                        <>
                          <Upload className='w-4 h-4 mr-2' />
                          Parse & Preview
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Preview Summary */}
            {qbPreview && (
              <div className='space-y-4'>
                <div className='flex flex-wrap gap-4 p-4 bg-white rounded-sm border border-green-200'>
                  <div className='text-center'>
                    <p className='text-2xl font-bold text-slate-800'>{qbPreview.totalRows}</p>
                    <p className='text-xs text-slate-500'>Total Invoices</p>
                  </div>
                  <div className='text-center'>
                    <p className='text-2xl font-bold text-green-600'>{qbPreview.matchedJobs}</p>
                    <p className='text-xs text-slate-500'>Jobs Matched</p>
                  </div>
                  <div className='text-center'>
                    <p className='text-2xl font-bold text-amber-600'>{qbPreview.unmatchedJobs}</p>
                    <p className='text-xs text-slate-500'>Unmatched</p>
                  </div>
                  <div className='text-center'>
                    <p className='text-2xl font-bold text-slate-800'>{formatCurrency(qbPreview.totalAmount)}</p>
                    <p className='text-xs text-slate-500'>Total Amount</p>
                  </div>
                  <div className='text-center'>
                    <p className='text-2xl font-bold text-blue-600'>{qbSelected.size}</p>
                    <p className='text-xs text-slate-500'>Selected</p>
                  </div>
                </div>

                {/* Editable Table */}
                <div className='border border-slate-200 rounded-sm overflow-hidden'>
                  <div className='overflow-x-auto max-h-[500px]'>
                    <Table>
                      <TableHeader className='bg-slate-50 sticky top-0'>
                        <TableRow>
                          <TableHead className='w-10'>
                            <Checkbox
                              checked={qbSelected.size === qbInvoices.length && qbInvoices.length > 0}
                              onCheckedChange={toggleQbSelectAll}
                              data-testid='qb-select-all'
                            />
                          </TableHead>
                          <TableHead className='min-w-[180px]'>Job</TableHead>
                          <TableHead className='min-w-[140px]'>Vendor</TableHead>
                          <TableHead className='min-w-[100px]'>Date</TableHead>
                          <TableHead className='min-w-[100px]'>Invoice #</TableHead>
                          <TableHead className='min-w-[100px]'>Type</TableHead>
                          <TableHead className='min-w-[200px]'>Description</TableHead>
                          <TableHead className='min-w-[100px] text-right'>Amount</TableHead>
                          <TableHead className='w-16'>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {qbInvoices.map((inv) => (
                          <TableRow 
                            key={inv.rowIndex}
                            className={!inv.jobMatched ? 'bg-amber-50' : qbSelected.has(inv.rowIndex) ? 'bg-green-50/50' : ''}
                            data-testid={`qb-row-${inv.rowIndex}`}
                          >
                            <TableCell>
                              <Checkbox
                                checked={qbSelected.has(inv.rowIndex)}
                                onCheckedChange={() => toggleQbSelect(inv.rowIndex)}
                                data-testid={`qb-checkbox-${inv.rowIndex}`}
                              />
                            </TableCell>
                            <TableCell>
                              <Select
                                value={inv.jobId || ''}
                                onValueChange={(val) => handleQbInvoiceChange(inv.rowIndex, 'jobId', val)}
                              >
                                <SelectTrigger 
                                  className={`h-8 text-xs rounded-sm ${!inv.jobMatched ? 'border-amber-400 bg-amber-50' : ''}`}
                                  data-testid={`qb-job-select-${inv.rowIndex}`}
                                >
                                  <SelectValue placeholder={inv.project || 'Select job...'} />
                                </SelectTrigger>
                                <SelectContent>
                                  {jobs.map((job) => (
                                    <SelectItem key={job.id} value={job.id}>
                                      {job.jobNumber} - {job.description?.substring(0, 30)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Input
                                value={inv.vendor}
                                onChange={(e) => handleQbInvoiceChange(inv.rowIndex, 'vendor', e.target.value)}
                                className='h-8 text-xs rounded-sm'
                                data-testid={`qb-vendor-${inv.rowIndex}`}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type='date'
                                value={inv.date}
                                onChange={(e) => handleQbInvoiceChange(inv.rowIndex, 'date', e.target.value)}
                                className='h-8 text-xs rounded-sm'
                                data-testid={`qb-date-${inv.rowIndex}`}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={inv.invoiceNumber}
                                onChange={(e) => handleQbInvoiceChange(inv.rowIndex, 'invoiceNumber', e.target.value)}
                                className='h-8 text-xs rounded-sm'
                                data-testid={`qb-invoice-num-${inv.rowIndex}`}
                              />
                            </TableCell>
                            <TableCell>
                              <Select
                                value={inv.type}
                                onValueChange={(val) => handleQbInvoiceChange(inv.rowIndex, 'type', val)}
                              >
                                <SelectTrigger className='h-8 text-xs rounded-sm' data-testid={`qb-type-${inv.rowIndex}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value='MATERIAL'>Material</SelectItem>
                                  <SelectItem value='EQUIPMENT'>Equipment</SelectItem>
                                  <SelectItem value='SUBCONTRACTOR'>Subcontractor</SelectItem>
                                  <SelectItem value='NON_BILLABLE'>Non-Billable</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Input
                                value={inv.description}
                                onChange={(e) => handleQbInvoiceChange(inv.rowIndex, 'description', e.target.value)}
                                className='h-8 text-xs rounded-sm'
                                data-testid={`qb-description-${inv.rowIndex}`}
                              />
                            </TableCell>
                            <TableCell className='text-right font-mono text-sm'>
                              {formatCurrency(inv.amount)}
                            </TableCell>
                            <TableCell>
                              {inv.jobMatched ? (
                                <CheckCircle className='w-4 h-4 text-green-600' />
                              ) : (
                                <AlertTriangle className='w-4 h-4 text-amber-500' title={inv.errors?.join(', ')} />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className='flex gap-3 justify-end'>
                  <Button
                    variant='outline'
                    onClick={handleQbCancel}
                    className='rounded-sm'
                    data-testid='qb-cancel-btn'
                  >
                    <X className='w-4 h-4 mr-2' />
                    Cancel
                  </Button>
                  <Button
                    onClick={handleQbImport}
                    disabled={qbImporting || qbSelected.size === 0}
                    className='bg-green-600 hover:bg-green-700 text-white rounded-sm'
                    data-testid='qb-import-btn'
                  >
                    {qbImporting ? (
                      <>
                        <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                        Importing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className='w-4 h-4 mr-2' />
                        Import {qbSelected.size} Invoice{qbSelected.size !== 1 ? 's' : ''}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Import Cards Grid */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
        {IMPORT_TYPES.map((type) => {
          const Icon = type.icon;
          const result = results[type.id];
          
          return (
            <Card key={type.id} className='border-slate-200 shadow-sm rounded-sm'>
              <CardHeader className='pb-3'>
                <div className='flex items-center gap-3'>
                  <div className={`p-2 rounded-sm ${type.color}`}>
                    <Icon className='w-5 h-5' />
                  </div>
                  <div>
                    <CardTitle className='text-lg font-bold'>{type.name}</CardTitle>
                    <CardDescription className='text-sm'>{type.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className='space-y-3'>
                {/* Result Display */}
                {result && !result.error && (
                  <div className='p-3 bg-slate-50 rounded-sm border border-slate-200'>
                    <div className='flex items-center gap-2 mb-2 flex-wrap'>
                      <CheckCircle className='w-4 h-4 text-green-600' />
                      <span className='font-bold text-green-700'>
                        {result.imported} imported
                      </span>
                      {result.crafts_created > 0 && (
                        <Badge className='bg-orange-100 text-orange-700 rounded-sm'>
                          {result.crafts_created} crafts auto-created
                        </Badge>
                      )}
                      {result.timesheets_created > 0 && (
                        <Badge className='bg-blue-100 text-blue-700 rounded-sm'>
                          {result.timesheets_created} timesheets created
                        </Badge>
                      )}
                    </div>
                    {result.errors?.length > 0 && (
                      <details className='text-sm'>
                        <summary className='cursor-pointer text-amber-600 font-medium'>
                          {result.errors.length} warnings/errors
                        </summary>
                        <ul className='mt-2 space-y-1 text-xs text-slate-600 max-h-32 overflow-y-auto'>
                          {result.errors.slice(0, 10).map((err, i) => (
                            <li key={`err-${i}-${err.slice(0, 20)}`} className='bg-amber-50 p-1 rounded'>{err}</li>
                          ))}
                          {result.errors.length > 10 && (
                            <li className='text-slate-500'>...and {result.errors.length - 10} more</li>
                          )}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
                
                {result?.error && (
                  <div className='p-3 bg-red-50 rounded-sm border border-red-200'>
                    <div className='flex items-center gap-2 text-red-700'>
                      <AlertTriangle className='w-4 h-4' />
                      <span className='text-sm font-medium'>{result.error}</span>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className='flex gap-2'>
                  <Button
                    onClick={() => handleDownloadTemplate(type.id)}
                    variant='outline'
                    size='sm'
                    className='flex-1 border-2 border-slate-200 hover:border-[#1A3668] rounded-sm'
                  >
                    <Download className='w-4 h-4 mr-1' />
                    Template
                  </Button>
                  
                  <input
                    type='file'
                    accept='.xlsx,.xls'
                    onChange={(e) => handleFileSelect(type.id, e)}
                    ref={(el) => fileInputRefs.current[type.id] = el}
                    className='hidden'
                    id={`file-${type.id}`}
                  />
                  
                  <Button
                    onClick={() => fileInputRefs.current[type.id]?.click()}
                    disabled={uploading === type.id}
                    size='sm'
                    className='flex-1 bg-[#1A3668] hover:bg-[#142A52] text-white rounded-sm'
                  >
                    {uploading === type.id ? (
                      <>
                        <Loader2 className='w-4 h-4 mr-1 animate-spin' />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload className='w-4 h-4 mr-1' />
                        Upload
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Instructions */}
      <Card className='mt-8 border-slate-200 shadow-sm rounded-sm'>
        <CardHeader>
          <CardTitle className='text-xl font-bold'>How to Import Data</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
            <div className='flex gap-3'>
              <div className='flex-shrink-0 w-8 h-8 rounded-full bg-[#1A3668] text-white flex items-center justify-center font-bold'>1</div>
              <div>
                <h4 className='font-bold text-slate-800'>Download Template</h4>
                <p className='text-sm text-slate-600'>Click "Template" to download an Excel file with the correct column headers and example data.</p>
              </div>
            </div>
            <div className='flex gap-3'>
              <div className='flex-shrink-0 w-8 h-8 rounded-full bg-[#1A3668] text-white flex items-center justify-center font-bold'>2</div>
              <div>
                <h4 className='font-bold text-slate-800'>Fill in Your Data</h4>
                <p className='text-sm text-slate-600'>Replace the example rows with your actual data. Keep the header row intact.</p>
              </div>
            </div>
            <div className='flex gap-3'>
              <div className='flex-shrink-0 w-8 h-8 rounded-full bg-[#1A3668] text-white flex items-center justify-center font-bold'>3</div>
              <div>
                <h4 className='font-bold text-slate-800'>Upload & Import</h4>
                <p className='text-sm text-slate-600'>Click "Upload" and select your filled Excel file. Review any errors after import.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone - Clear All Timesheets (Owner Only) */}
      {isOwner && (
        <Card className='border-red-200 shadow-sm rounded-sm bg-red-50/30'>
          <CardHeader className='pb-4'>
            <div className='flex items-center gap-3'>
              <AlertOctagon className='w-6 h-6 text-red-600' />
              <div>
                <CardTitle className='text-red-800'>Danger Zone</CardTitle>
                <CardDescription className='text-red-600'>
                  Irreversible actions - use with caution
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-white rounded-sm border border-red-200'>
              <div>
                <h4 className='font-bold text-red-800'>Clear All Timesheets</h4>
                <p className='text-sm text-red-600'>
                  Delete all timesheets, labor entries, and equipment entries. This cannot be undone.
                </p>
              </div>
              <Button
                onClick={() => setClearTimesheetsDialog(true)}
                variant='outline'
                className='border-red-300 text-red-600 hover:bg-red-100 hover:border-red-400 rounded-sm whitespace-nowrap'
                data-testid='clear-all-timesheets-btn'
              >
                <Trash2 className='w-4 h-4 mr-2' />
                Clear All Timesheets
              </Button>
            </div>
            
            <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-white rounded-sm border border-amber-200'>
              <div>
                <h4 className='font-bold text-amber-800'>Cleanup Orphaned Data</h4>
                <p className='text-sm text-amber-600'>
                  Remove labor entries with missing timesheets or deleted jobs. Fixes incorrect billing recaps.
                </p>
              </div>
              <Button
                onClick={() => setCleanupOrphansDialog(true)}
                variant='outline'
                className='border-amber-300 text-amber-600 hover:bg-amber-100 hover:border-amber-400 rounded-sm whitespace-nowrap'
                data-testid='cleanup-orphans-btn'
              >
                <AlertTriangle className='w-4 h-4 mr-2' />
                Cleanup Orphaned Data
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Clear Timesheets Confirmation Dialog */}
      <AlertDialog open={clearTimesheetsDialog} onOpenChange={setClearTimesheetsDialog}>
        <AlertDialogContent className='rounded-sm'>
          <AlertDialogHeader>
            <AlertDialogTitle className='text-red-800 flex items-center gap-2'>
              <AlertOctagon className='w-5 h-5' />
              Clear All Timesheets?
            </AlertDialogTitle>
            <AlertDialogDescription className='text-slate-600'>
              This will <strong>permanently delete</strong> all timesheets, labor entries, and equipment entries for your company.
              <br /><br />
              <span className='text-red-600 font-medium'>This action cannot be undone.</span>
              <br /><br />
              Are you sure you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className='rounded-sm' disabled={clearingTimesheets}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearAllTimesheets}
              disabled={clearingTimesheets}
              className='bg-red-600 hover:bg-red-700 rounded-sm'
              data-testid='confirm-clear-timesheets-btn'
            >
              {clearingTimesheets ? (
                <>
                  <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                  Clearing...
                </>
              ) : (
                'Yes, Clear All Timesheets'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cleanup Orphans Confirmation Dialog */}
      <AlertDialog open={cleanupOrphansDialog} onOpenChange={setCleanupOrphansDialog}>
        <AlertDialogContent className='rounded-sm'>
          <AlertDialogHeader>
            <AlertDialogTitle className='text-amber-800 flex items-center gap-2'>
              <AlertTriangle className='w-5 h-5' />
              Cleanup Orphaned Data?
            </AlertDialogTitle>
            <AlertDialogDescription className='text-slate-600'>
              This will remove labor entries that reference deleted timesheets or jobs.
              <br /><br />
              This helps fix incorrect billing recaps caused by data inconsistencies.
              <br /><br />
              <span className='text-amber-600 font-medium'>This action cannot be undone, but only affects orphaned records.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className='rounded-sm' disabled={cleaningOrphans}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCleanupOrphans}
              disabled={cleaningOrphans}
              className='bg-amber-600 hover:bg-amber-700 rounded-sm'
              data-testid='confirm-cleanup-orphans-btn'
            >
              {cleaningOrphans ? (
                <>
                  <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                  Cleaning...
                </>
              ) : (
                'Yes, Cleanup Orphaned Data'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Upload Dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, type: null, file: null })}>
        <AlertDialogContent className='rounded-sm'>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Import</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to import <strong>{confirmDialog.file?.name}</strong> as {getTypeInfo(confirmDialog.type)?.name}.
              <br /><br />
              This will add new records to your database. Existing records with the same name/number will be skipped.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className='rounded-sm'>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUpload}
              className='bg-[#1A3668] hover:bg-[#142A52] rounded-sm'
            >
              Import Data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* QBO Bill Matching Dialog */}
      <AlertDialog open={!!matchingBill} onOpenChange={(open) => !open && setMatchingBill(null)}>
        <AlertDialogContent className='rounded-sm max-w-2xl'>
          <AlertDialogHeader>
            <AlertDialogTitle className='text-emerald-800 flex items-center gap-2'>
              <FileSpreadsheet className='w-5 h-5' />
              Match QuickBooks Bill to Pending PO
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className='text-slate-600'>
                {matchingBill && (
                  <div className='bg-emerald-50 border border-emerald-200 rounded-sm p-3 mt-2'>
                    <div className='grid grid-cols-2 gap-2 text-sm'>
                      <div><strong>Vendor:</strong> {matchingBill.vendorName}</div>
                      <div><strong>Amount:</strong> ${matchingBill.totalAmount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                      <div><strong>Doc #:</strong> {matchingBill.docNumber || 'N/A'}</div>
                      <div><strong>Date:</strong> {matchingBill.txnDate}</div>
                    </div>
                  </div>
                )}
                
                <div className='mt-4'>
                  <label className='text-sm font-medium text-slate-700 mb-2 block'>
                    Select Pending Material PO to Match:
                  </label>
                  <Select value={selectedPoForMatch} onValueChange={setSelectedPoForMatch}>
                    <SelectTrigger className='rounded-sm'>
                      <SelectValue placeholder='Choose a pending PO...' />
                    </SelectTrigger>
                    <SelectContent>
                      {pendingPOs
                        .filter(po => 
                          !matchingBill || 
                          po.vendorName.toLowerCase().includes(matchingBill.vendorName?.toLowerCase() || '') ||
                          matchingBill.vendorName?.toLowerCase().includes(po.vendorName.toLowerCase())
                        )
                        .map(po => (
                          <SelectItem key={po.id} value={po.id}>
                            <div className='flex items-center gap-2'>
                              <span className='font-mono text-xs'>{po.poNumber}</span>
                              <span>-</span>
                              <span>{po.vendorName}</span>
                              <span className='text-slate-500'>({po.jobNumber})</span>
                              <span className='font-bold'>${po.approxAmount?.toLocaleString()}</span>
                            </div>
                          </SelectItem>
                        ))
                      }
                      {pendingPOs.length === 0 && (
                        <div className='p-2 text-sm text-slate-500'>No pending POs available</div>
                      )}
                    </SelectContent>
                  </Select>
                  
                  {matchingBill && pendingPOs.filter(po => 
                    po.vendorName.toLowerCase().includes(matchingBill.vendorName?.toLowerCase() || '') ||
                    matchingBill.vendorName?.toLowerCase().includes(po.vendorName.toLowerCase())
                  ).length === 0 && pendingPOs.length > 0 && (
                    <p className='text-xs text-amber-600 mt-2'>
                      <AlertTriangle className='w-3 h-3 inline mr-1' />
                      No pending POs found matching vendor "{matchingBill.vendorName}". 
                      Showing all pending POs above.
                    </p>
                  )}
                </div>

                <div className='mt-3 p-3 bg-slate-50 rounded-sm text-xs text-slate-600'>
                  <strong>What happens when you match:</strong>
                  <ul className='list-disc list-inside mt-1'>
                    <li>The pending PO status changes to "Invoiced"</li>
                    <li>The actual amount from QuickBooks is recorded</li>
                    <li>A material charge is created for the job</li>
                  </ul>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className='rounded-sm' disabled={matchingInProgress}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleMatchBillToPO}
              disabled={matchingInProgress || !selectedPoForMatch}
              className='bg-emerald-600 hover:bg-emerald-700 rounded-sm'
              data-testid='confirm-match-bill-btn'
            >
              {matchingInProgress ? (
                <>
                  <Loader2 className='w-4 h-4 animate-spin mr-2' />
                  Matching...
                </>
              ) : (
                'Match & Import Bill'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
